import moment from 'moment';
import {
  BotComplexParams,
  Candle,
  ComplexParamsBundle,
  TimeFrame,
} from '../screens/Detail/types';
import { Transaction } from '../screens/Overview/types';
import { Transaction as ProfitTransaction } from '../screens/ProfitCalculations/types';
import { formatPercentage, formatPrice } from './NumberFormatter';
import { getTimeFrameIntervalTimestamp } from '../screens/Detail/Charts/PriceChart/helpers';
import { CustomTransaction } from 'grts-utils/src/types/Transaction';
import {
  CompactTransaction,
  DetailCompactTransaction,
  DetailTransaction,
} from '../types/subscription';
import { Bot } from 'grts-utils/src/types/Bot';

export const formatCorrectDecimals = (value: number, decimals: number) => {
  if (!value) {
    return 0;
  }

  const valueString = value.toString();
  const pointIndex = valueString.indexOf('.');

  if (pointIndex === -1) {
    // TODO, add error, wrong value / no decimals
    return value;
  }

  const amountOfDecimals = valueString.length - pointIndex - 1;

  if (amountOfDecimals < decimals) {
    return value;
  }

  const formattedValue = valueString.substring(0, pointIndex + decimals + 1);
  return parseFloat(formattedValue);
};

export const findTransactionWave = (
  candles: Candle[],
  startTimestamp: number,
  endTimestamp: number,
) => {
  /** If we have a buy,
   * we need the wave from buy to next sell;
   * and a wave red (or small area before the start of green wave)
   * and wave green
   * and (little red at the end)
   *
   *
   * If we have a sell,
   * we need the wave from previous buy to sell;
   * and a wave green (or small period)
   * and wave red
   * and little green at the end
   */
  if (candles.length === 0) {
    return candles;
  }

  const startTransactionCandleIndex = candles.findIndex(
    (c) =>
      c.startTimestamp <= startTimestamp && startTimestamp <= c.endTimestamp,
  );

  const endTransactionCandleIndex = candles.findIndex(
    (c) => c.startTimestamp <= endTimestamp && endTimestamp <= c.endTimestamp,
  );

  if (startTransactionCandleIndex === -1 || endTransactionCandleIndex === -1) {
    return [];
  }

  return candles.slice(
    Math.max(0, startTransactionCandleIndex - 1),
    Math.min(endTransactionCandleIndex + 2, candles.length),
  );
};

/* ==============================================   createHeikinCandle   ============================================== */
export const createHeikinCandle = (
  currentNormalCandle: Candle | null,
  previousHeikinCandle?: Candle | null,
) => {
  if (!currentNormalCandle) {
    return null;
  }

  const open =
    ((previousHeikinCandle?.open ?? currentNormalCandle.open) +
      (previousHeikinCandle?.close ?? currentNormalCandle.close)) /
    2;
  const close =
    (currentNormalCandle.open +
      currentNormalCandle.high +
      currentNormalCandle.low +
      currentNormalCandle.close) /
    4;
  const low = currentNormalCandle.low;
  const high = currentNormalCandle.high;

  const direction = open > close ? 'down' : 'up';

  const heikinCandle = {
    ...currentNormalCandle,
    low:
      direction === 'up'
        ? open < low
          ? open
          : low
        : close < low
        ? close
        : low,
    open,
    close,
    high:
      direction === 'up'
        ? close > high
          ? close
          : high
        : open > high
        ? open
        : high,
    direction,
    startOfNewTrend: previousHeikinCandle
      ? previousHeikinCandle.direction !== direction
      : null,
    didMakeDecision: null,
    decision: null,
  };

  return heikinCandle;
};

/* ==============================================   transformToHeikinCandles   ============================================== */
/** Difference here is that you can pass a list of normal candles instead of 1 */
export const transformToHeikinCandles = (normalCandles: Candle[]): Candle[] => {
  const heikinCandles: Candle[] = [];

  normalCandles.forEach((candle) => {
    const heikinCandle = createHeikinCandle(
      candle,
      heikinCandles[heikinCandles.length - 1],
    );
    heikinCandles.push(heikinCandle!);
  });

  return heikinCandles;
};

export const formatTransactionType = (
  transactions: ProfitTransaction[],
  bot: Bot,
) => {
  return transactions.map((t, i) => {
    return {
      index: t.index || i + 1,
      id: Math.floor(Math.random() * 9999999999),
      botId: 1,
      baseAsset: bot.baseAsset,
      quoteAsset: bot.quoteAsset,
      transactionIndex: t.id,
      compactTransactionId: t.compactTransactionId
        ? t.compactTransactionId.toString()
        : null,
      orderId: null,
      orderType: t.orderType,
      transactionType: t.transactionType,
      orderPrice: t.orderPrice,
      assetQuantity: t.assetQuantity,
      stableQuantity: t.stableQuantity,
      currentAssetBalance: t.balanceInAsset,
      currentStableBalance: t.balanceInUsdt,
      totalBalance: t.balanceInUsdt + t.balanceInAsset * t.orderPrice,
      currentFee: t.currentFee,
      totalFee: t.totalFee,
      isComplete: t.isComplete ? 1 : 0,
      success: t.success,
      profit: t.orderType === 'BUY' ? t.profitInAsset : t.profitInUsdt,
      currentProfit: null,
      transactionTimestamp: t.timestamp,
      transactionDate: t.date,
      initialVolume: 0,
      initialVolumeAsset: 'USD',
      currentCbr: null,
      cbrt: 0,
      shorterTimeFrameInterval: 1,
      mrcEjectedPrice: null,
      mrcAcceptedPrice: null,
      multiOrderVolume: null,
      multiOrderIndex: null,
      customBalanceUsed: null,
      customBalanceSizeUsed: null,
      extraInfo: null,
      actions: null,
      averageOrderPrice: (t as any).averageOrderPrice,
      candles: t.candles,
    } as Transaction;
  });
};

export const isCustomTransaction = (
  transaction: Transaction | CompactTransaction | CustomTransaction,
): transaction is CustomTransaction =>
  transaction.transactionType.indexOf('BALANCE') !== -1;

export const generateTooltipLabel = (
  transaction: DetailCompactTransaction | CustomTransaction,
  bot?: Bot,
  isProfitCalcs?: boolean,
) => {
  const formattedDate = moment(transaction.transactionTimestamp).format(
    'DD MMM YYYY',
  );

  if (isCustomTransaction(transaction)) {
    const isStableTransaction =
      transaction.transactionType.indexOf('STABLE') !== -1;
    const quantity = isStableTransaction
      ? `Quantity: ${transaction.stableQuantity} ${bot?.quoteAsset}`
      : `Quantity: ${transaction.assetQuantity} ${bot?.baseAsset}`;

    return `Date: ${formattedDate}\n${quantity}`;
  }

  const profit = transaction.profit || 0;
  const orderType = transaction.orderType;

  return `${transaction.index}. ${transaction.orderType}${
    typeof isProfitCalcs === 'undefined' ? '' : isProfitCalcs ? ' (PC)' : ' (R)'
  }\nDate: ${formattedDate}\nPrice: ${formatPrice(
    transaction.averageOrderPrice || transaction.orderPrice,
  )}\n${orderType === 'BUY' ? 'A.' : 'S.'} Profit: ${formatPercentage(profit)}`;
};

export const fillTransactionIndexes = (
  transactions: Array<Transaction | DetailTransaction>,
  compactTransactions: Array<Transaction | DetailCompactTransaction>,
) => {
  if (!transactions) {
    return transactions;
  }

  const allCompactTransactions = [...compactTransactions].sort(
    (a, b) => a.transactionTimestamp - b.transactionTimestamp,
  );
  const allTransactions = [...transactions].sort(
    (a, b) => a.transactionTimestamp - b.transactionTimestamp,
  );

  let compactTransactionIndex = 0;

  for (let i = 0; i < allCompactTransactions.length; i++) {
    const compactTransaction = allCompactTransactions[i];

    if (isCustomTransaction(compactTransaction)) {
      continue;
    }

    compactTransactionIndex++;
    compactTransaction.index = compactTransactionIndex.toString();

    /** Do the same but for individual transactions */
    const individualTransactions = allTransactions.filter(
      (t) => t.compactTransactionId === compactTransaction.compactTransactionId,
    );

    for (let j = 0; j < individualTransactions.length; j++) {
      const individualTransaction = individualTransactions[j];
      individualTransaction.index = `${compactTransactionIndex}.${j + 1}`;
    }
  }

  return [allTransactions, allCompactTransactions];
};

export const compareComplexParamsBundle = (
  complexParamsBundle: ComplexParamsBundle,
  transaction: DetailCompactTransaction,
) => {
  return (
    !!transaction.complexParams &&
    !!transaction.timeFrames &&
    (transaction.complexParams as BotComplexParams).cbrt ===
      complexParamsBundle.complexParams.cbrt &&
    (transaction.complexParams as BotComplexParams).shorterTimeFrameInterval ===
      complexParamsBundle.complexParams.shorterTimeFrameInterval &&
    (transaction.complexParams as BotComplexParams).supportCbrtRange ===
      complexParamsBundle.complexParams.supportCbrtRange &&
    (transaction.complexParams as BotComplexParams).supportMrcState ===
      complexParamsBundle.complexParams.supportMrcState &&
    transaction.timeFrames.higherT === complexParamsBundle.timeFrames.higher &&
    transaction.timeFrames.shorterT ===
      complexParamsBundle.timeFrames.shorter &&
    transaction.timeFrames.multiT === complexParamsBundle.timeFrames.multiOrder
  );
};

export const getFormatUnit = (
  startTimestamp: number,
  endTimestamp: number,
  includeHours = true,
) => {
  const range = endTimestamp - startTimestamp;

  return range < getTimeFrameIntervalTimestamp(TimeFrame.FiveDays) &&
    includeHours
    ? 'hh:mm'
    : range < getTimeFrameIntervalTimestamp(TimeFrame.TwoMonths) * 2
    ? 'DD MMM'
    : 'MMM YYYY';
};

export const createXTickValues = (
  startTimstamp: number,
  endTimstamp?: number,
  amount = 12,
) => {
  /** If we don't have a period
   * Check based on total range
   */
  const ticks = [];

  const end = endTimstamp || new Date().valueOf();

  // When we have one datapoint and the timestamp is end of current day
  if (startTimstamp > end) {
    return [startTimstamp];
  }

  const range = end - startTimstamp;
  const interval = range / amount;

  const start = startTimstamp - interval / 2;

  ticks.push(start);

  for (let i = 1; i <= amount + 1; i++) {
    ticks.push(start + interval * i);
  }

  /** We always want to see the start of the month / week / day...
   * But this is quite tricky here
   * We want to see how big the range is to know which start period to take
   */
  const formatUnit = getFormatUnit(startTimstamp, end);

  for (let i = 0; i < ticks.length; i++) {
    const tick: number = ticks[i];

    switch (formatUnit) {
      case 'DD MMM':
        ticks[i] = moment(tick).utc().startOf('day').valueOf();
        break;

      case 'hh:mm':
        ticks[i] = moment(tick).utc().startOf('hour').valueOf();
        break;

      case 'MMM YYYY':
        ticks[i] = moment(tick).utc().startOf('month').valueOf();
        break;
    }
  }

  return ticks;
};

export const findClosestCandleTimestamp = (
  timestamp: number,
  candleDataPoints: Candle[],
) => {
  if (!candleDataPoints) {
    return timestamp;
  }

  return (
    candleDataPoints.find(
      (c) => c.startTimestamp <= timestamp && timestamp <= c.endTimestamp,
    )?.startTimestamp || 0
  );
};
